import React, { useMemo, useState, useEffect } from "react";
import { Input, Button } from "@elaniintc/goes-design-system";
import ComparativeValueInputCalendar from "@/workflow/components/configurations/if/ComparativeValueInput.calendar";
//import { variableOptions } from "../utils/constants";
import type { CalculationElement } from "../types";
import {
  DropdownList,
  type DropdownListGroup,
  type IDropdownListItem,
} from "@/components/ui/dropdown-list/dropdown-list";
import { nodeVariableToGroups, type NodeVariable } from "../../../types";
import { useMaskito } from "@maskito/react";
import { maskitoTimeOptionsGenerator } from "@maskito/kit";

interface DateOperationInputProps {
  variables: NodeVariable[];
  /** Tipo de operación de fecha */
  operation: string;
  /** Elementos para gestionar los valores */
  elements: CalculationElement[];
  /** Callback para actualizar elementos */
  onElementsChange: (elements: CalculationElement[]) => void;
  /** Grupo de elementos */
  group?:
    | "down-left"
    | "down"
    | "down-right"
    | "left"
    | "right"
    | "up"
    | "up-left"
    | "up-right"
    | "single"
    | "middle"
    | "middle-right"
    | "middle-left";
}

/**
 * Componente para renderizar inputs específicos de operaciones de fecha
 */
export const DateOperationInput: React.FC<DateOperationInputProps> = ({
  operation,
  elements,
  variables,
  onElementsChange,
}) => {
  const updateElement = (property: keyof CalculationElement, value: string) => {
    const elementId =
      elements[0]?.id || Math.random().toString(36).substr(2, 9);
    const updatedElement: CalculationElement = {
      ...elements[0],
      id: elementId,
      type: elements[0]?.type || "inp",
      [property]: value,
    };
    onElementsChange([updatedElement]);
  };

  const [variableSearch, setVariableSearch] = useState("");
  const variableGroups = useMemo(() => {
    const groups = nodeVariableToGroups(variables);
    return groups
      .map((group) => ({
        ...group,
        items: group.items.filter(
          (item) =>
            item.detail === "date" &&
            ((item.label as string)
              .toLowerCase()
              .includes(variableSearch.toLowerCase()) ||
              group.groupLabels.some((label) =>
                label.toLowerCase().includes(variableSearch.toLowerCase()),
              )),
        ),
      }))
      .filter((group) => group.items.length > 0);
  }, [variables, variableSearch]);

  const [_selectedVariable, setSelectedVariable] =
    useState<IDropdownListItem>();
  const [_selectedGroup, setSelectedGroup] = useState<DropdownListGroup>();

  // Sincronizar el valor deserializado del elemento con el dropdown visual
  useEffect(() => {
    if (
      elements[0]?.value &&
      elements[0]?.type === "var" &&
      variableGroups.length > 0
    ) {
      let found = false;
      for (const group of variableGroups) {
        const variable = group.items.find(
          (item) => item.value === elements[0].value,
        );
        if (variable) {
          setSelectedVariable(variable);
          setSelectedGroup(group);
          found = true;
          break;
        }
      }
      // Si no se encuentra la variable, limpiar selección visual
      if (!found) {
        setSelectedVariable(undefined);
        setSelectedGroup(undefined);
      }
    } else {
      // Si no hay elemento o no es una variable, limpiar selección visual
      setSelectedVariable(undefined);
      setSelectedGroup(undefined);
    }
  }, [elements, variableGroups]);

  const onVariableSelection = (variableId: string, groupId: string) => {
    const group = variableGroups.find((g) => g.groupId === groupId);
    const variable = group?.items.find((item) => item.value === variableId);
    setSelectedVariable(variable);
    setSelectedGroup(group);
    if (variable) {
      updateElement("value", variable.value);
    } else {
      updateElement("value", "");
    }
  };

  const timeMask = useMaskito({
    options: maskitoTimeOptionsGenerator({
      mode: "HH:MM",
      timeSegmentMinValues: {
        hours: 0,
        minutes: 0,
      },
      timeSegmentMaxValues: {
        hours: 999,
        minutes: 59,
      },
    }),
  });

  // DateDiff operation
  if (operation === "date_diff") {
    return (
      <>
        <Button
          variant="quiet"
          group={"down-left"}
          className="h-full px-3"
          style={{ minHeight: 40 }}
          onClick={() => {
            const currentType = elements[0]?.type || "inp";
            const newType = currentType === "inp" ? "var" : "inp";
            if (newType === "var") {
              setSelectedVariable(undefined);
              setSelectedGroup(undefined);
            }
            onElementsChange([
              {
                id: elements[0]?.id || Math.random().toString(36).substr(2, 9),
                type: newType,
                value: "",
              },
            ]);
          }}
        >
          {elements[0]?.type || "inp"}
        </Button>
        {!elements[0] || elements[0].type === "inp" ? (
          <div className="flex-1 h-full">
            <ComparativeValueInputCalendar
              defaultDate={elements[0]?.value || ""}
              group="down-right"
              onChangeDate={(isoDate) => {
                onElementsChange([
                  {
                    id:
                      elements[0]?.id ||
                      Math.random().toString(36).substr(2, 9),
                    type: "inp",
                    value: isoDate,
                  },
                ]);
              }}
            />
          </div>
        ) : (
          <DropdownList
            group="down-right"
            options={variableGroups}
            selectedValue={_selectedVariable}
            selectedGroup={_selectedGroup}
            toggleSelection={(variable, group) => {
              onVariableSelection(variable.value, group?.groupId || "");
            }}
            widthClassName="flex-1"
            placeholder="Elegir variable"
            search={{
              placeholder: "Buscar variable",
              onSearch: (query) => {
                setVariableSearch(query);
              },
              query: variableSearch,
            }}
          />
        )}
      </>
    );
  }

  // Función para normalizar el valor de horas
  const normalizeHoursValue = (value: string) => {
    if (!value) return "";
    // Si es solo números (ej: "1", "23"), convertir a formato HH:MM (ej: "1:00", "23:00")
    if (/^[0-9]{1,3}$/.test(value)) {
      return `${value}:00`;
    }
    // Si ya está en formato HH:MM, mantenerlo
    return value;
  };

  // Time inputs for add/subtract operations
  if (operation === "date_add" || operation === "date_subtract") {
    return (
      <>
        <Input
          className="w-1/4"
          group={"down-left"}
          placeholder="Horas y minutos"
          value={elements[0]?.hours || ""}
          ref={timeMask}
          onChange={(e) => {
            updateElement("hours", e.target.value);
          }}
          onBlur={(e) => {
            const normalizedValue = normalizeHoursValue(e.target.value);
            if (normalizedValue !== e.target.value) {
              updateElement("hours", normalizedValue);
            }
          }}
        />
        <Input
          className="w-1/4"
          group="middle"
          placeholder="Días"
          maxLength={5}
          value={elements[0]?.days || ""}
          onChange={(e) => {
            // allow just numbers
            if (!/^\d*$/.test(e.target.value)) return;
            updateElement("days", e.target.value);
          }}
        />
        <Input
          className="w-1/4"
          group="middle"
          placeholder="Meses"
          maxLength={5}
          value={elements[0]?.months || ""}
          onChange={(e) => {
            // allow just numbers
            if (!/^\d*$/.test(e.target.value)) return;
            updateElement("months", e.target.value);
          }}
        />
        <Input
          className="w-1/4"
          group="down-right"
          placeholder="Años"
          maxLength={3}
          value={elements[0]?.years || ""}
          onChange={(e) => {
            // allow just numbers
            if (!/^\d*$/.test(e.target.value)) return;
            updateElement("years", e.target.value);
          }}
        />
      </>
    );
  }

  return null;
};
